/** A chunky keypad. Kids need to type 2443, and a native number input is both
 *  fiddly and off-brand. */

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Check, Delete } from 'lucide-preact';
import { play } from '../engine/audio.ts';
import { ToyButton } from './controls.tsx';

interface Props {
  title: string;
  value: number;
  min: number;
  max: number;
  onDone: (value: number) => void;
  onCancel: () => void;
}

export function NumberPad({ title, value, min, max, onDone, onCancel }: Props): JSX.Element {
  const [text, setText] = useState(String(value));
  const parsed = Number(text);
  const valid = text.length > 0 && Number.isFinite(parsed) && parsed >= min && parsed <= max;

  const press = (digit: string) => {
    const next = (text === '0' ? '' : text) + digit;
    if (Number(next) > max) {
      play('thud', 0.4);
      return;
    }
    play('tap', 0.5);
    setText(next);
  };

  return (
    <div class="sheet-scrim" onClick={onCancel} role="presentation">
      <div class="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 class="sheet__title">{title}</h2>
        <p class="pad__value" aria-live="polite">
          {text || '0'}
        </p>
        <div class="pad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" class="pad__key" onClick={() => press(d)}>
              {d}
            </button>
          ))}
          <button
            type="button"
            class="pad__key"
            aria-label="Delete"
            onClick={() => {
              play('tap', 0.4);
              setText((t) => t.slice(0, -1));
            }}
          >
            <Delete size={24} strokeWidth={2.5} />
          </button>
          <button type="button" class="pad__key" onClick={() => press('0')}>
            0
          </button>
          <button
            type="button"
            class="pad__key pad__key--go"
            aria-label="Done"
            disabled={!valid}
            onClick={() => {
              play('pop', 0.6);
              onDone(Math.min(max, Math.max(min, parsed)));
            }}
          >
            <Check size={26} strokeWidth={3} />
          </button>
        </div>
        <div class="sheet__actions">
          <ToyButton label="Cancel" onClick={onCancel} />
        </div>
      </div>
    </div>
  );
}
